/**
 * dees-mosaic — sway-like tiling layout model. A layout is a tree of split
 * containers whose leaves reference host-defined surfaces. The component is
 * controlled: hosts own the layout object and receive complete next layouts
 * through events; the tree helpers in tree.ts are pure and DOM-free.
 */

export type TMosaicOrientation = 'row' | 'column';

export interface IMosaicLeafNode {
  type: 'leaf';
  /** stable node id — the slot contract is slot="tile-<id>" */
  id: string;
  /** host-defined surface key, e.g. 'chat' | 'theia' | 'browser' */
  surface: string;
  /** optional per-leaf override of the surface meta title */
  title?: string;
}

export interface IMosaicSplitNode {
  type: 'split';
  id: string;
  orientation: TMosaicOrientation;
  /** length >= 2 after normalization */
  children: TMosaicNode[];
  /** same length as children; each >= minimum share; sums to 1 */
  ratios: number[];
}

export type TMosaicNode = IMosaicLeafNode | IMosaicSplitNode;

export interface IMosaicLayout {
  /** null = empty workspace (the `empty` slot renders) */
  root: TMosaicNode | null;
}

export interface IMosaicSurfaceMeta {
  title: string;
  /** dees-icon name, e.g. 'lucide:Globe' */
  icon?: string;
  /** default true */
  closeable?: boolean;
}

export type TMosaicDropEdge = 'left' | 'right' | 'top' | 'bottom';

export type TMosaicLayoutChangeReason = 'resize' | 'drop' | 'close' | 'equalize';

export interface IMosaicLayoutChangeDetail {
  /** the complete next layout — the host applies and persists it */
  layout: IMosaicLayout;
  reason: TMosaicLayoutChangeReason;
}

export interface IMosaicTileEventDetail {
  leafId: string;
  surface: string;
}

export interface IMosaicDragStateDetail {
  dragging: boolean;
  leafId?: string;
}
